import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, Loader2, Download, FileSpreadsheet, FileDown } from "lucide-react";
import { useReportExport } from "@/hooks/useReportExport";
import { FileDropzone } from "@/components/ui/FileDropzone";
import { useClaudeAI } from "@/hooks/useClaudeAI";
import { summarizeForAI, downloadCsv, downloadXlsx, type ParsedFile } from "@/lib/file-parsing";
import { toast } from "sonner";

const INDUSTRIES = [
  { value: "general", label: "General / Universal" },
  { value: "finance", label: "Finance" },
  { value: "ecommerce", label: "E-commerce" },
  { value: "logistics", label: "Logistics" },
  { value: "realestate", label: "Real Estate" },
  { value: "technology", label: "Technology" },
];

const MODEL_TYPES = [
  { value: "regression", label: "Regression (numerical)" },
  { value: "classification", label: "Classification (category)" },
  { value: "forecasting", label: "Forecasting (time series)" },
];

interface PredictiveResult {
  model_type: string;
  target: string;
  feature_importance: { feature: string; importance: number }[];
  predictions: { row_index: number; prediction: any; confidence: number }[];
  metrics: { accuracy: number; error_rate: number; confidence: number };
  top_drivers: string[];
  summary: string;
}

export const PredictiveModelingAI = () => {
  const [parsed, setParsed] = useState<ParsedFile | null>(null);
  const [target, setTarget] = useState("");
  const [modelType, setModelType] = useState("regression");
  const [industry, setIndustry] = useState("general");
  const [result, setResult] = useState<PredictiveResult | null>(null);
  const { call, isLoading } = useClaudeAI();
  const { generateReport, isGenerating } = useReportExport();

  const downloadPdfReport = () => {
    if (!result || !parsed?.rows?.length) return;
    generateReport({
      title: "Predictive model report",
      datasetName: parsed.fileName || "Model dataset",
      rows: parsed.rows as Record<string, unknown>[],
      industry,
      context: `Model type: ${result.model_type || modelType}. Target: ${result.target || target}. ${result.summary ?? ""} Top drivers: ${(result.top_drivers ?? []).join(", ")}.`,
      extraMetrics: [
        { label: "Target column", value: result.target || target },
        { label: "Model type", value: result.model_type || modelType },
        { label: "Accuracy", value: pct(result.metrics?.accuracy) },
        { label: "Confidence", value: pct(result.metrics?.confidence) },
        { label: "Predictions", value: String(result.predictions?.length ?? 0) },
      ],
    });
  };

  const columns = useMemo(() => parsed?.columns ?? [], [parsed]);

  const run = async () => {
    if (!parsed || parsed.kind !== "table") { toast.error("Upload a CSV or Excel file"); return; }
    if (!parsed.rows?.length) { toast.error("Dataset is empty"); return; }
    if (!target) { toast.error("Pick the target column"); return; }

    setResult(null);
    const res = await call<PredictiveResult>({
      feature: "predictive_modeling",
      dataset: summarizeForAI(parsed),
      input: `Target column: ${target}. Model type: ${modelType}. Industry: ${industry}.`,
      options: { target, modelType, industry, fileName: parsed.fileName },
    });
    if (res.success && res.json) setResult(res.json);
  };

  const downloadResults = (format: "csv" | "xlsx") => {
    if (!result?.predictions?.length) return;
    const rows = result.predictions.map((p) => ({
      row_index: p.row_index,
      prediction: typeof p.prediction === "object" ? JSON.stringify(p.prediction) : p.prediction,
      confidence: p.confidence,
    }));
    const name = `predictions-${Date.now()}`;
    if (format === "csv") downloadCsv(name, rows);
    else downloadXlsx(name, rows);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-primary" /> Predictive Modeling
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <FileDropzone onParsed={(p) => { setParsed(p); setTarget(""); setResult(null); }} label="Upload dataset" />

        <div className="grid gap-4 md:grid-cols-3">
          <div>
            <label className="text-sm font-medium">Target column</label>
            <Select value={target} onValueChange={setTarget} disabled={!columns.length}>
              <SelectTrigger><SelectValue placeholder={columns.length ? "Select column" : "Upload a file first"} /></SelectTrigger>
              <SelectContent>
                {columns.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium">Model type</label>
            <Select value={modelType} onValueChange={setModelType}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {MODEL_TYPES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium">Industry</label>
            <Select value={industry} onValueChange={setIndustry}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {INDUSTRIES.map((i) => <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <Button onClick={run} disabled={isLoading || !parsed || !target} className="w-full">
          {isLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Training & predicting…</> : "Train & Predict"}
        </Button>

        {result && (
          <div className="space-y-4 pt-2">
            <div className="grid gap-3 md:grid-cols-3">
              <Metric label="Accuracy" value={pct(result.metrics?.accuracy)} />
              <Metric label="Error rate" value={pct(result.metrics?.error_rate)} />
              <Metric label="Confidence" value={pct(result.metrics?.confidence)} />
            </div>

            <div className="rounded-lg border p-4">
              <h4 className="font-semibold mb-2">Plain-English Summary</h4>
              <p className="text-sm">{result.summary}</p>
            </div>

            <Button variant="outline" onClick={downloadPdfReport} disabled={isGenerating}>
              {isGenerating ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <FileDown className="h-4 w-4 mr-2" />
              )}
              {isGenerating ? "Building report…" : "Download as PDF"}
            </Button>

            <div className="rounded-lg border p-4">
              <h4 className="font-semibold mb-2">Top 3 Drivers</h4>
              <div className="flex flex-wrap gap-2">
                {(result.top_drivers ?? []).slice(0, 3).map((d) => <Badge key={d} variant="secondary">{d}</Badge>)}
              </div>
            </div>

            <div className="rounded-lg border">
              <div className="flex items-center justify-between p-3 border-b">
                <h4 className="font-semibold">Predictions ({result.predictions?.length ?? 0})</h4>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => downloadResults("csv")}>
                    <Download className="h-3 w-3 mr-1" /> CSV
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => downloadResults("xlsx")}>
                    <FileSpreadsheet className="h-3 w-3 mr-1" /> Excel
                  </Button>
                </div>
              </div>
              <div className="max-h-64 overflow-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-muted">
                    <tr><th className="p-2 text-left">Row</th><th className="p-2 text-left">Prediction</th><th className="p-2 text-left">Confidence</th></tr>
                  </thead>
                  <tbody>
                    {result.predictions?.slice(0, 200).map((p) => (
                      <tr key={p.row_index} className="border-t">
                        <td className="p-2">{p.row_index}</td>
                        <td className="p-2">{typeof p.prediction === "object" ? JSON.stringify(p.prediction) : String(p.prediction)}</td>
                        <td className="p-2">{pct(p.confidence)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const Metric = ({ label, value }: { label: string; value: string }) => (
  <div className="rounded-lg border bg-muted/40 p-3 text-center">
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className="text-xl font-bold">{value}</p>
  </div>
);

const pct = (n: number | undefined) => (typeof n === "number" ? `${Math.round(n * 100)}%` : "—");
